import { BaseMarkerController } from '../base/baseMarkerController'
import { PolygonMarker } from './polygonMarker'
import { MarkerModes } from '../../types'
import { PolygonMarkerEdge } from './PolygonMarkerEdge'
import { PolygonMarkerVertex } from './PolygonMarkerVertex'
import { PolygonMarkerStateManager } from './polygonMarkerStateManager'

class PolygonMarkerController extends BaseMarkerController<PolygonMarker> {
  declare marker: PolygonMarker

  constructor(marker: PolygonMarker) {
    super()
    this.marker = marker
  }

  mouseDraggedEvent(): void {
    super.mouseDraggedEvent()
    const marker = this.marker
    const state = marker.state
    const { isPressed, pressedHandle } = state

    if (!isPressed) return
    if (!pressedHandle) {
      state.moveMarker(this.mouseDelta.x, this.mouseDelta.y)
    } else {
      handleHandleDrag(state, this)
    }
    marker.handles.syncVertexHandles()
  }

  mouseMovedEvent(): void {
    super.mouseMovedEvent()
    const marker = this.marker
    const state = marker.state

    if (state.mode === MarkerModes.Edit) {
      marker.updateMousedOverEdge()
    }
  }

  mouseReleasedEvent(): void {
    super.mouseReleasedEvent()
    const marker = this.marker
    const state = marker.state
    const mouseX = this.mouseCoordinateX
    const mouseY = this.mouseCoordinateY

    if (state.mode === MarkerModes.Create) {
      if (!marker.isComplete()) {
        state.appendVertex(mouseX, mouseY)
      }
      if (marker.isComplete()) {
        state.mode = MarkerModes.Edit
        marker.handles.populate()
        marker.markerCreatedCallback(this.marker)
      }
    }
  }
}

const handleUpdateFunctionMapping = {
  vertex: handleVertexDrag,
  edge: handleMidpointDrag,
}

/**
 * Gets the type of handle that is being dragged; whether an endpoint or vertex handle
 * @param pressedHandle
 * @param vertices
 * @param edges
 * @returns
 */
function getPressedHandleType(pressedHandle, vertices, edges) {
  const isVertexHandle = vertices.find((vertex: PolygonMarkerVertex) => vertex.handle === pressedHandle)
  const isEdgeHandle = edges.find((edge: PolygonMarkerEdge) => edge.midpointHandle === pressedHandle)
  if (isVertexHandle) return 'vertex'
  if (isEdgeHandle) return 'edge'
  return
}

/**
 * Handler function for vertex handle
 * @param {PolygonMarkerStateManager} state
 * @param {PolygonMarkerController} controller
 */
function handleVertexDrag(state: PolygonMarkerStateManager, controller: PolygonMarkerController) {
  const { vertices, pressedHandle } = state
  const handleVertex = vertices.find((vertex: PolygonMarkerVertex) => vertex.handle === pressedHandle)
  const mouseDelta = controller.mouseDelta
  state.moveVertex(handleVertex, mouseDelta.x, mouseDelta.y)
}

/**
 * Handler function for midpoint handle
 * @param {PolygonMarkerStateManager} state
 * @param {PolygonMarkerController} controller
 */
function handleMidpointDrag(state: PolygonMarkerStateManager, controller: PolygonMarkerController) {
  const { edges, vertices, pressedHandle } = state
  const { mouseCoordinateX, mouseCoordinateY } = controller
  const handleEdge = edges.find((edge: PolygonMarkerEdge) => edge.midpointHandle === pressedHandle)
  const linkedVertices = vertices.reduce((acc, vertex, i) => {
    let isLinked = false
    vertex.edges.forEach((edge) => {
      if (edge === handleEdge) isLinked = true
    })
    if (isLinked) acc.push(i)
    return acc
  }, [])
  const newVertexIndex =
    linkedVertices[0] === 0 && linkedVertices[1] === vertices.length - 1 ? linkedVertices[1] + 1 : linkedVertices[1]
  state.insertVertex(mouseCoordinateX, mouseCoordinateY, newVertexIndex)
  const newVertex = vertices[newVertexIndex]
  //Ressign handle to vertex so that the newly created vertice can be moved
  newVertex.handle = pressedHandle
  handleEdge.midpointHandle = null
}

/**
 * Takes in state and controller to determine which handle type is being dragged an appropriately triage it to
 * handler function
 * @param {PolygonMarkerStateManager} state
 * @param {PolygonMarkerController} controller
 */
function handleHandleDrag(state: PolygonMarkerStateManager, controller: PolygonMarkerController) {
  const { pressedHandle, vertices, edges } = state
  const handleType = getPressedHandleType(pressedHandle, vertices, edges)
  const handlerFunction = handleUpdateFunctionMapping[handleType]
  if (handlerFunction) handlerFunction(state, controller)
}

export { PolygonMarkerController }
